import { Grid, Box, IconButton, styled, TextareaAutosize, Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { AiFillLinkedin, AiOutlineMail } from 'react-icons/ai';
import { BsTelephoneFill } from 'react-icons/bs';
import { FaRegAddressCard } from 'react-icons/fa';
import { GrLocation } from 'react-icons/gr';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { RiFileUserLine } from 'react-icons/ri';
import {
  ILocation,
  IWorkspacePaymentInfo,
  saveUser,
  captureWorkspacePaymentInfo,
  IUserDTO,
} from 'wisestep-api';
import {
  Item,
  WsButton,
  WsColors,
  WsIconText,
  WsTypography,
  WsTypoVariants,
  WsLocationSearch,
} from 'wisestep-base';

import { success, failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import RecruiterDetailsFields from '../RecruiterDetailsFields';

export const StyledTextareaAutosize = styled(TextareaAutosize)(() => ({
  minWidth: '100%',
  backgroundColor: WsColors.TertiaryShades50,
  border: 'none',
  outline: '2px solid' + WsColors.TertiaryShades200,
  borderRadius: '5px',
}));

interface ProfileProps {
  loggedInUser?: IUserDTO | null;
  workspacePaymentInfo?: IWorkspacePaymentInfo | null;
  onSave(): void;
}

const UserProfile = ({ loggedInUser, workspacePaymentInfo, onSave }: ProfileProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);

  const profileDetailsDefaultValues = {
    id: loggedInUser?.id,
    firstName: loggedInUser?.firstName,
    lastName: loggedInUser?.lastName,
    phoneNumber: loggedInUser?.phoneNumber,
    email: loggedInUser?.email,
    alternateEmails: loggedInUser?.alternateEmails ?? [],
    linkedInProfileLink: loggedInUser?.linkedInProfileLink,
    about: loggedInUser?.about,
    alternatePhoneNumbers: loggedInUser?.alternatePhoneNumbers ?? [],
    location: loggedInUser?.location,
    isTeamSelectionMandatory: loggedInUser?.isTeamSelectionMandatory,
    zipCode: workspacePaymentInfo?.pincode ?? loggedInUser?.zipCode,
    address: workspacePaymentInfo?.address ?? loggedInUser?.location?.area,
    gst: workspacePaymentInfo?.gst,
  };

  const formOptions = {
    defaultValues: profileDetailsDefaultValues,
  };
  const methods = useForm(formOptions);
  const { handleSubmit } = methods;

  const saveInPostgres = async (data) => {
    const temp = workspacePaymentInfo ?? {};
    temp.country = data?.location?.country ?? '';
    temp.gst = data?.gst ?? '';
    temp.address = data?.address ?? '';
    temp.pincode = data?.zipCode ?? '';
    captureWorkspacePaymentInfo(temp);
  };

  const onFormSubmit = async (data) => {
    const tempData = loggedInUser;
    if (tempData && data) {
      const addressInfo: ILocation | null = data.location
        ? {
            city: data.location.city,
            continent: data.location.continent,
            continentCode: data.location.continentCode,
            country: data.location.country,
            countryCode: data.location.countryCode,
            state: data.location.state,
            stateCode: data.location.stateCode,
          }
        : null;
      if (addressInfo) {
        addressInfo.area = data.address;
      }
      tempData.firstName = data.firstName;
      tempData.lastName = data.lastName;
      tempData.phoneNumber = data.phoneNumber;
      tempData.email = data.email;
      tempData.alternateEmails = data.alternateEmails?.size == 0 ? '' : data.alternateEmails;
      tempData.linkedInProfileLink = data.linkedInProfileLink;
      tempData.about = data.about;
      tempData.alternatePhoneNumbers = data.alternatePhoneNumber;
      tempData.location = addressInfo;
      tempData.zipCode = data.zipCode;
      try {
        await saveInPostgres(data);
        await saveUser(tempData);
        setNotification(success('Profile details updated successfully!'));
        onSave();
      } catch (error) {
        setNotification(failed('Failed to save profile details!'));
      }
    }
  };

  return (
    <Item sx={{ backgroundColor: WsColors.White }}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Box>
            <Grid container pb={2}>
              <Grid item xs={6} justifyContent="flex-start">
                <WsTypography variant={WsTypoVariants.TITLE}>User Information</WsTypography>
              </Grid>
              <Grid item container xs={6} justifyContent="flex-end">
                <WsButton size="large" type="submit">
                  Save
                </WsButton>
              </Grid>
            </Grid>
            <Grid container spacing={2} pt={1}>
              <Grid item xs={6}>
                <RecruiterDetailsFields
                  name="firstName"
                  iconText={
                    <WsIconText
                      icon={<RiFileUserLine size={20} />}
                      text="First Name*"
                      textSize={WsTypoVariants.BODY1}
                    />
                  }
                  type="name"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <RecruiterDetailsFields
                  name="lastName"
                  iconText={
                    <WsIconText
                      icon={<RiFileUserLine size={20} />}
                      text="Last Name*"
                      textSize={WsTypoVariants.BODY1}
                    />
                  }
                  type="name"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <RecruiterDetailsFields
                  name="phoneNumber"
                  iconText={
                    <WsIconText
                      icon={<BsTelephoneFill size={15} />}
                      text="Phone Number*"
                      textSize={WsTypoVariants.BODY1}
                    />
                  }
                  type="text"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <RecruiterDetailsFields
                  name="email"
                  iconText={
                    <WsIconText
                      icon={<AiOutlineMail size={20} />}
                      text="Email"
                      textSize={WsTypoVariants.BODY1}
                    />
                  }
                  disable={true}
                  type="email"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <RecruiterDetailsFields
                  name="alternateEmails"
                  iconText={
                    <WsIconText
                      icon={<AiOutlineMail size={20} />}
                      text="Alternate Email"
                      textSize={WsTypoVariants.BODY1}
                    />
                  }
                  type="email"
                />
              </Grid>
              <Grid item xs={6}>
                <RecruiterDetailsFields
                  name="linkedInProfileLink"
                  iconText={
                    <WsIconText
                      icon={<AiFillLinkedin size={20} />}
                      text="Linkedin Profile Link"
                      textSize={WsTypoVariants.BODY1}
                    />
                  }
                  type="text"
                  defaultValue={loggedInUser?.linkedInProfileLink ?? ''}
                />
              </Grid>
              <Grid item xs={6}>
                <RecruiterDetailsFields
                  name="address"
                  iconText={
                    <WsIconText
                      icon={<FaRegAddressCard size={20} />}
                      text="Address"
                      textSize={WsTypoVariants.BODY1}
                    />
                  }
                  type="text"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <Stack direction="column">
                  <Grid pl={1} pt={1}>
                    <WsIconText
                      icon={<GrLocation size={15} />}
                      text="Location"
                      textSize={WsTypoVariants.BODY1}
                    />
                  </Grid>
                  <WsLocationSearch
                    name="location"
                    placeHolder=""
                    size="medium"
                    multiSelect={false}
                  />
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <RecruiterDetailsFields
                  name="zipCode"
                  iconText={
                    <WsIconText
                      icon={<GrLocation size={15} />}
                      text="Zip Code"
                      textSize={WsTypoVariants.BODY1}
                    />
                  }
                  type="text"
                />
              </Grid>
              <Grid item xs={6}>
                <RecruiterDetailsFields
                  name="gst"
                  iconText={
                    <WsIconText icon={<></>} text="GSTIN Number" textSize={WsTypoVariants.BODY1} />
                  }
                  type="text"
                />
              </Grid>
              <Grid item xs={12}>
                <IconButton>
                  <WsIconText
                    icon={<IoMdInformationCircleOutline size={20} />}
                    text="About"
                    textSize={WsTypoVariants.BODY1}
                  />
                </IconButton>
                <StyledTextareaAutosize
                  name="about"
                  minRows={10}
                  placeholder=""
                  defaultValue={loggedInUser?.about ?? ''}
                />
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </Item>
  );
};

export default UserProfile;
